/**
 * The story feed — the column that is 80% of this product.
 *
 * Three jobs, and nothing else: put the pages in reading order, render each chapter as a
 * scene, and fetch older ones as the reader climbs. It is deliberately not where the turn
 * lives — the live scene, the phase line and any failure are `children`, appended after the
 * last stored chapter, so the turn machinery renders *through* the feed rather than the feed
 * being special-cased around it.
 *
 * It used to take the world state too, to join location display names for the dateline. The
 * API denormalizes the dateline onto the chapter row now (U4), so the feed needs one input.
 *
 * **`spent` is the fourth job and the smallest one**: a dead run's scrollback is drawn as a
 * record rather than as a live game (`TODO.md` §7.1). It applies to the chapters and not to
 * `children`, which is exactly why it is a wrapper here instead of a class on `#story-feed` in
 * `PlayRoute` — the death card is a child, and dimming the ending along with the story would
 * defeat the point of dimming the story.
 */
import { useMemo } from 'react';
import type { Chapter } from '@/api/generated';
import { ChapterScene } from '@/features/story/ChapterScene';
import { toReadingOrder } from '@/features/story/readingOrder';
import { useOlderChapters, type OlderChapters } from '@/features/story/useOlderChapters';
import { useT } from '@/i18n/context';

interface StoryFeedProps {
  /**
   * Pages as `useChapters` returns them: page 0 is the newest, and each page is newest-first.
   * Flattening and reversing is this component's job so no caller has to remember the order.
   */
  pages: readonly { chapters: Chapter[] }[];
  /** The `before` cursor. Omitted where there is no paging to do (tests, a fresh story). */
  older?: OlderChapters;
  /** The turn in progress: `LiveScene`, `PhaseLine`, `TurnFailure` — and the death card. */
  children?: React.ReactNode;
  /** The character is dead and the last chapter has landed: desaturate the scrollback. */
  spent?: boolean;
}

export function StoryFeed({ pages, older, children, spent = false }: StoryFeedProps) {
  const t = useT();
  const chapters = useMemo(() => toReadingOrder(pages), [pages]);
  const sentinel = useOlderChapters(older, chapters[0]?.number);

  return (
    <div className="mx-auto w-full max-w-[var(--measure)] px-5 py-12">
      {/*
        Above the oldest chapter: the trigger, and the only status the reader gets about it.
        The `div` is always rendered while there is more to load — the observer needs
        something with a position, and a zero-height node would sit exactly on a boundary.
      */}
      {older?.has ? (
        <div
          ref={sentinel}
          className="text-fg-faint mb-[var(--space-chapter)] text-center font-mono"
          style={{ fontSize: 'var(--text-meta)' }}
        >
          {older.loading ? t('ui.chapter.loading_older') : t('ui.chapter.load_older')}
        </div>
      ) : chapters.length > 0 ? (
        <p
          className="text-fg-faint mb-[var(--space-chapter)] text-center font-mono"
          style={{ fontSize: 'var(--text-meta)' }}
        >
          {t('ui.chapter.story_begins')}
        </p>
      ) : null}

      {/*
        One element whether or not it is spent, so React keeps the DOM node across the commit
        that kills the character: a wrapper that appeared only when `spent` did would remount
        every chapter on the page at exactly the moment the reader is being asked to sit still,
        and would restart the very transition it exists to run.

        It is skipped entirely on a story with no chapters, and that is not tidiness — an empty
        wrapper would still be `:first-child`, and `Scene`'s `first:mt-0` is what puts the
        opening turn at the top of an empty screen instead of 3.5rem down it.
      */}
      {chapters.length > 0 ? (
        <div className={spent ? 'feed-spent' : undefined}>
          {chapters.map((chapter) => (
            <ChapterScene key={chapter.number} chapter={chapter} />
          ))}
        </div>
      ) : null}

      {children}
    </div>
  );
}
